import React, { useState, useEffect, useMemo } from 'react';
import {
    Save,
    AlertCircle,
    CheckCircle,
    RotateCcw,
    ChevronDown,
    ChevronRight,
    Loader2,
    Calculator,
    Shield,
    Sparkles
} from 'lucide-react';
import { apiClient, ScoringConfigResponse, ScoringConfigField, UpdateScoringConfigRequest } from '../services/api';

// Default threshold values - should match backend scoring-thresholds.ts
const DEFAULT_LOW_MEDIUM_BOUNDARY = 15;
const DEFAULT_MEDIUM_HIGH_BOUNDARY = 40;

interface ScoringEditorProps {
    user: any;
}

const CATEGORY_LABELS: Record<string, { label: string; description?: string; isHighlighted?: boolean }> = {
    Critical: { label: 'Critical & Legal Issues' },
    Category: { label: 'Licence Category Requirements' },
    PenaltyPoints: { label: 'Penalty Points' },
    Endorsement: { label: 'Endorsement Recency' },
    SeriousOffence: { label: 'Serious Offences' },
    CPC: { label: 'CPC Requirements' },
    Tachograph: { label: 'Tachograph Card' },
    Medical: { label: 'Medical Checks' },
    NewDriver: { label: 'New Driver Rules' },
    Professional: { label: 'Professional Driver Checks' },
    Pattern: { label: 'Pattern Detection', isHighlighted: true },
    SiteRisk: { label: 'Site Risk Factors' },
};

/**
 * Human-readable labels for field names
 */
const FIELD_LABELS: Record<string, string> = {
    // Critical/Legal
    invalidLicenceScore: 'Invalid Licence',
    disqualifiedScore: 'Disqualified',
    pcvMissingScore: 'PCV Category Missing',
    hgvMissingScore: 'HGV Category Missing',
    restriction101Score: 'Restriction 101 (No hire/reward)',
    carFleetMissingBScore: 'Category B Missing',
    carFleetProvisionalBScore: 'Provisional Category B',
    // Penalty Points
    penaltyPointsLowScore: '1-3 Points (Low)',
    penaltyPointsMediumScore: '4-6 Points (Medium)',
    penaltyPointsElevatedScore: '7-8 Points (Elevated)',
    penaltyPointsHighScore: '9-11 Points (High)',
    // Endorsement Recency
    endorsementVeryRecentScore: 'Very Recent (< 6 months)',
    endorsementRecentScore: 'Recent (6-12 months)',
    endorsementModerateScore: 'Moderate (1-2 years)',
    // Serious Offence
    seriousOffenceScore: 'Serious Offence (DR/DD/CD)',
    // CPC
    cpcMissingScore: 'CPC Missing',
    cpcExpiredScore: 'CPC Expired',
    cpcExpiringScore: 'CPC Expiring Soon',
    cpcGeneralExpiredScore: 'CPC Expired (General)',
    cpcGeneralExpiringScore: 'CPC Expiring (General)',
    // Tachograph
    tachoMissingScore: 'Tacho Card Missing',
    tachoExpiredScore: 'Tacho Card Expired',
    tachoExpiringScore: 'Tacho Card Expiring',
    tachoInvalidStatusScore: 'Tacho Invalid Status',
    tachoGeneralExpiredScore: 'Tacho Expired (General)',
    tachoGeneralExpiringScore: 'Tacho Expiring (General)',
    tachoGeneralInvalidStatusScore: 'Tacho Invalid (General)',
    // Medical
    medicalOverdueScore: 'Medical Check Overdue',
    medicalDueSoonScore: 'Medical Due Soon',
    medicalUnknownScore: 'Medical Status Unknown',
    // New Driver
    newDriverNearThresholdScore: 'Near 6-Point Threshold',
    // Professional
    professionalBaselineScore: 'Professional Driver Baseline',
    professionalLostCategoryScore: 'Lost Required Category',
    professionalDowngradedScore: 'Category Downgraded',
    professionalRestrictionsScore: 'Has Restrictions',
    professionalProvisionalScore: 'Provisional Entitlement',
    professionalExpiredScore: 'Entitlement Expired',
    // Pattern Analysis
    patternEscalatingScore: 'Escalating Severity',
    patternFrequencyScore: 'High Frequency',
    patternRepeatScore: 'Repeat Offences',
    patternClusterScore: 'Clustered Offences',
    patternDeteriorationScore: 'Score Deterioration',
    patternRestrictionsScore: 'New Restrictions',
    // Site Risk
    siteRiskNeverCheckedScore: 'Never Checked Driver',
    siteRiskOverduePenalty: 'Overdue Check Penalty',
    // Risk Level Thresholds
    riskLevelLowMediumBoundary: 'Low/Medium Boundary',
    riskLevelMediumHighBoundary: 'Medium/High Boundary',
};

const ScoringEditor: React.FC<ScoringEditorProps> = ({ user }) => {
    // State
    const [config, setConfig] = useState<ScoringConfigResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // UI State
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['PenaltyPoints']));
    const [showResetAllConfirmation, setShowResetAllConfirmation] = useState(false);
    const [fieldToReset, setFieldToReset] = useState<string | null>(null);

    // Pending changes tracking
    const [pendingChanges, setPendingChanges] = useState<Record<string, number | null>>({});

    // Threshold validation error
    const [thresholdError, setThresholdError] = useState<string | null>(null);

    // Check for unsaved changes
    const hasChanges = useMemo(() => {
        return Object.keys(pendingChanges).length > 0;
    }, [pendingChanges]);

    // Check if save is allowed (has changes and no validation errors)
    const canSave = useMemo(() => {
        return hasChanges && !thresholdError;
    }, [hasChanges, thresholdError]);

    // Warn before leaving with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasChanges]);

    // Load config on mount
    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getScoringConfig();
            setConfig(response);
            setPendingChanges({});
        } catch (err: any) {
            setError(err.message || 'Failed to load scoring configuration');
            console.error('Error loading scoring config:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Group fields by category
    const groupedFields = useMemo(() => {
        if (!config) return {};

        const groups: Record<string, ScoringConfigField[]> = {};
        config.fields.forEach(field => {
            if (!groups[field.category]) {
                groups[field.category] = [];
            }
            groups[field.category].push(field);
        });
        return groups;
    }, [config]);

    // Toggle category expansion
    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    // Get effective value (pending change or current)
    const getEffectiveValue = (field: ScoringConfigField): number => {
        if (pendingChanges[field.fieldName] !== undefined) {
            return pendingChanges[field.fieldName] ?? field.defaultValue;
        }
        return field.currentValue;
    };

    // Check if field has pending change
    const hasFieldChanged = (field: ScoringConfigField): boolean => {
        return pendingChanges[field.fieldName] !== undefined;
    };

    // Handle field value change
    const handleFieldChange = (fieldName: string, value: string) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        const field = config?.fields.find(f => f.fieldName === fieldName);
        if (!field) return;

        // Validate range (0-100)
        const clampedValue = Math.max(0, Math.min(100, numValue));

        // If setting back to current value, remove from pending changes
        if (clampedValue === field.currentValue) {
            setPendingChanges(prev => {
                const next = { ...prev };
                delete next[fieldName];
                return next;
            });
        } else {
            setPendingChanges(prev => ({
                ...prev,
                [fieldName]: clampedValue
            }));
        }
    };

    // Reset single field to default
    const resetFieldToDefault = async (fieldName: string) => {
        try {
            setIsSaving(true);
            setError(null);
            await apiClient.resetScoringField(fieldName);

            // Reload config
            await loadConfig();
            setSuccessMessage(`${FIELD_LABELS[fieldName] || fieldName} reset to default`);
            setFieldToReset(null);

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset field');
        } finally {
            setIsSaving(false);
        }
    };

    // Discard pending change for a field
    const discardFieldChange = (fieldName: string) => {
        setPendingChanges(prev => {
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
        // Clear threshold error if discarding a threshold field
        if (fieldName === 'riskLevelLowMediumBoundary' || fieldName === 'riskLevelMediumHighBoundary') {
            setThresholdError(null);
        }
    };

    // Get effective threshold values (pending or current)
    const getEffectiveLowMedium = (): number => {
        if (pendingChanges.riskLevelLowMediumBoundary !== undefined) {
            return pendingChanges.riskLevelLowMediumBoundary ?? DEFAULT_LOW_MEDIUM_BOUNDARY;
        }
        return config?.riskLevelThresholds?.lowMediumBoundary ?? DEFAULT_LOW_MEDIUM_BOUNDARY;
    };

    const getEffectiveMediumHigh = (): number => {
        if (pendingChanges.riskLevelMediumHighBoundary !== undefined) {
            return pendingChanges.riskLevelMediumHighBoundary ?? DEFAULT_MEDIUM_HIGH_BOUNDARY;
        }
        return config?.riskLevelThresholds?.mediumHighBoundary ?? DEFAULT_MEDIUM_HIGH_BOUNDARY;
    };

    // Handle threshold change with validation
    const handleThresholdChange = (field: 'riskLevelLowMediumBoundary' | 'riskLevelMediumHighBoundary', value: string) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) return;

        // Clamp to valid range
        let clampedValue: number;
        if (field === 'riskLevelLowMediumBoundary') {
            clampedValue = Math.max(1, Math.min(99, numValue));
        } else {
            clampedValue = Math.max(2, Math.min(100, numValue));
        }

        // Calculate what the effective values would be
        const effectiveLowMedium = field === 'riskLevelLowMediumBoundary'
            ? clampedValue
            : getEffectiveLowMedium();
        const effectiveMediumHigh = field === 'riskLevelMediumHighBoundary'
            ? clampedValue
            : getEffectiveMediumHigh();

        // Validate: low/medium boundary must be less than medium/high boundary
        if (effectiveLowMedium >= effectiveMediumHigh) {
            setThresholdError('Low/Medium boundary must be less than Medium/High boundary');
        } else {
            setThresholdError(null);
        }

        // Get current value from config
        const currentValue = field === 'riskLevelLowMediumBoundary'
            ? config?.riskLevelThresholds?.lowMediumBoundary ?? DEFAULT_LOW_MEDIUM_BOUNDARY
            : config?.riskLevelThresholds?.mediumHighBoundary ?? DEFAULT_MEDIUM_HIGH_BOUNDARY;

        // If setting back to current value, remove from pending changes
        if (clampedValue === currentValue) {
            setPendingChanges(prev => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        } else {
            setPendingChanges(prev => ({
                ...prev,
                [field]: clampedValue
            }));
        }
    };

    // Check if threshold has pending change
    const hasThresholdChanged = (field: 'riskLevelLowMediumBoundary' | 'riskLevelMediumHighBoundary'): boolean => {
        return pendingChanges[field] !== undefined;
    };

    // Check if threshold is customized (saved in DB)
    const isThresholdCustomized = (field: 'riskLevelLowMediumBoundary' | 'riskLevelMediumHighBoundary'): boolean => {
        if (!config) return false;
        return field === 'riskLevelLowMediumBoundary'
            ? config.riskLevelThresholds.isLowMediumCustomized
            : config.riskLevelThresholds.isMediumHighCustomized;
    };

    // Save all pending changes
    const handleSave = async () => {
        if (!canSave) return;

        // Double-check threshold validation before saving
        if (thresholdError) {
            setError(thresholdError);
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            const updates: UpdateScoringConfigRequest = {};
            Object.entries(pendingChanges).forEach(([key, value]) => {
                updates[key] = value;
            });

            await apiClient.updateScoringConfig(updates);

            // Reload config
            await loadConfig();
            setSuccessMessage('Scoring configuration saved successfully');

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save configuration');
        } finally {
            setIsSaving(false);
        }
    };

    // Reset all to defaults
    const handleResetAll = async () => {
        try {
            setIsSaving(true);
            setError(null);
            await apiClient.resetScoringConfig();

            // Reload config
            await loadConfig();
            setSuccessMessage('All scoring values reset to defaults');
            setShowResetAllConfirmation(false);

            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset configuration');
        } finally {
            setIsSaving(false);
        }
    };

    // Discard all pending changes
    const discardAllChanges = () => {
        setPendingChanges({});
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    <span className="ml-3 text-gray-600">Loading scoring configuration...</span>
                </div>
            </div>
        );
    }

    // Error state (initial load failed)
    if (error && !config) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-center py-12">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                    <span className="ml-3 text-red-600">{error}</span>
                </div>
            </div>
        );
    }

    if (!config) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: '#E8F5E9' }}>
                            <Calculator className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <p className="text-lg font-semibold text-gray-900">{config.subtypeLabel} Scoring</p>
                        </div>
                    </div>
                    {config.hasAnyCustomizations && (
                        <span className="px-2.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                            Customised
                        </span>
                    )}
                </div>
                {/* Risk Level Scale */}
                <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                    <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#69BE28' }}></span>
                            <span>0-{getEffectiveLowMedium() - 1} Low</span>
                        </span>
                        <span className="flex items-center space-x-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
                            <span>{getEffectiveLowMedium()}-{getEffectiveMediumHigh() - 1} Medium</span>
                        </span>
                        <span className="flex items-center space-x-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                            <span>{getEffectiveMediumHigh()}+ High</span>
                        </span>
                    </div>
                    <span className="flex items-center space-x-1 text-gray-500">
                        <Shield className="w-3 h-3" />
                        <span>Critical issues always score 100</span>
                    </span>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="ml-2 text-sm text-red-700">{error}</span>
                </div>
            )}
            {successMessage && (
                <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="ml-2 text-sm text-green-700">{successMessage}</span>
                </div>
            )}

            {/* Content */}
            <div className="p-6 space-y-3">
                {/* Threshold Settings */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => toggleCategory('_thresholds')}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            {expandedCategories.has('_thresholds') ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <span
                                className="w-2 h-2 rounded-full"
                                style={{
                                    backgroundColor:
                                        hasThresholdChanged('riskLevelLowMediumBoundary') ||
                                        hasThresholdChanged('riskLevelMediumHighBoundary') ||
                                        isThresholdCustomized('riskLevelLowMediumBoundary') ||
                                        isThresholdCustomized('riskLevelMediumHighBoundary')
                                            ? '#F59E0B'
                                            : '#69BE28'
                                }}
                            ></span>
                            <span className="font-medium text-gray-900">Risk Level Thresholds</span>
                        </div>
                        <span className="text-xs text-gray-500">2</span>
                    </button>
                    {expandedCategories.has('_thresholds') && (
                        <div className="p-4 border-t border-gray-200">
                            {/* Threshold validation error */}
                            {thresholdError && (
                                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-center">
                                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                    <span className="ml-2 text-sm text-red-700">{thresholdError}</span>
                                </div>
                            )}

                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-2 font-medium text-gray-500">Boundary</th>
                                        <th className="text-center py-2 font-medium text-gray-500 w-24">Score</th>
                                        <th className="text-center py-2 font-medium text-gray-500 w-20">Default</th>
                                        <th className="text-right py-2 font-medium text-gray-500 w-28">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-gray-100">
                                        <td className="py-3">
                                            <span className="font-medium text-gray-900">Low → Medium</span>
                                            <p className="text-xs text-gray-500">Scores below this = Low risk</p>
                                        </td>
                                        <td className="py-3 text-center">
                                            <input
                                                type="number"
                                                min="1"
                                                max="99"
                                                value={getEffectiveLowMedium()}
                                                onChange={(e) => handleThresholdChange('riskLevelLowMediumBoundary', e.target.value)}
                                                className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                                                    hasThresholdChanged('riskLevelLowMediumBoundary')
                                                        ? 'border-amber-400 bg-amber-50'
                                                        : thresholdError
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                }`}
                                            />
                                        </td>
                                        <td className="py-3 text-center text-gray-500">{DEFAULT_LOW_MEDIUM_BOUNDARY}</td>
                                        <td className="py-3 text-right">
                                            {hasThresholdChanged('riskLevelLowMediumBoundary') ? (
                                                <button
                                                    onClick={() => discardFieldChange('riskLevelLowMediumBoundary')}
                                                    className="text-xs text-amber-600 hover:text-amber-700"
                                                >
                                                    Discard
                                                </button>
                                            ) : isThresholdCustomized('riskLevelLowMediumBoundary') ? (
                                                <button
                                                    onClick={() => setFieldToReset('riskLevelLowMediumBoundary')}
                                                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center justify-end space-x-1"
                                                >
                                                    <RotateCcw className="w-3 h-3" />
                                                    <span>Reset</span>
                                                </button>
                                            ) : (
                                                <span className="text-xs text-green-600 flex items-center justify-end space-x-1">
                                                    <CheckCircle className="w-3 h-3" />
                                                    <span>Default</span>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-3">
                                            <span className="font-medium text-gray-900">Medium → High</span>
                                            <p className="text-xs text-gray-500">Scores at or above = High risk</p>
                                        </td>
                                        <td className="py-3 text-center">
                                            <input
                                                type="number"
                                                min="2"
                                                max="100"
                                                value={getEffectiveMediumHigh()}
                                                onChange={(e) => handleThresholdChange('riskLevelMediumHighBoundary', e.target.value)}
                                                className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                                                    hasThresholdChanged('riskLevelMediumHighBoundary')
                                                        ? 'border-amber-400 bg-amber-50'
                                                        : thresholdError
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                }`}
                                            />
                                        </td>
                                        <td className="py-3 text-center text-gray-500">{DEFAULT_MEDIUM_HIGH_BOUNDARY}</td>
                                        <td className="py-3 text-right">
                                            {hasThresholdChanged('riskLevelMediumHighBoundary') ? (
                                                <button
                                                    onClick={() => discardFieldChange('riskLevelMediumHighBoundary')}
                                                    className="text-xs text-amber-600 hover:text-amber-700"
                                                >
                                                    Discard
                                                </button>
                                            ) : isThresholdCustomized('riskLevelMediumHighBoundary') ? (
                                                <button
                                                    onClick={() => setFieldToReset('riskLevelMediumHighBoundary')}
                                                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center justify-end space-x-1"
                                                >
                                                    <RotateCcw className="w-3 h-3" />
                                                    <span>Reset</span>
                                                </button>
                                            ) : (
                                                <span className="text-xs text-green-600 flex items-center justify-end space-x-1">
                                                    <CheckCircle className="w-3 h-3" />
                                                    <span>Default</span>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Score Categories */}
                {Object.entries(groupedFields).map(([category, fields]) => {
                    const categoryInfo = CATEGORY_LABELS[category] || { label: category, description: '' };
                    const hasCustomizations = fields.some(f => f.isCustomized || hasFieldChanged(f));
                    const isHighlighted = categoryInfo.isHighlighted;

                    return (
                        <div
                            key={category}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                            <button
                                onClick={() => toggleCategory(category)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center space-x-2">
                                    {expandedCategories.has(category) ? (
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                    )}
                                    <span
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: hasCustomizations ? '#F59E0B' : '#69BE28' }}
                                    ></span>
                                    <span className="font-medium text-gray-900">{categoryInfo.label}</span>
                                    {isHighlighted && (
                                        <Sparkles className="w-3.5 h-3.5" style={{ color: '#69BE28' }} />
                                    )}
                                </div>
                                <span className="text-xs text-gray-500">
                                    {fields.length}
                                </span>
                            </button>
                            {expandedCategories.has(category) && (
                                <div className="p-4 border-t border-gray-200">
                                    {categoryInfo.description && (
                                        <p className="text-sm text-gray-600 mb-4">{categoryInfo.description}</p>
                                    )}
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 font-medium text-gray-500">Field</th>
                                                <th className="text-center py-2 font-medium text-gray-500 w-24">Score</th>
                                                <th className="text-center py-2 font-medium text-gray-500 w-20">Default</th>
                                                <th className="text-right py-2 font-medium text-gray-500 w-32">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fields.map((field) => {
                                                const effectiveValue = getEffectiveValue(field);
                                                const changed = hasFieldChanged(field);
                                                const isCustomized = field.isCustomized || changed;

                                                return (
                                                    <tr key={field.fieldName} className="border-b border-gray-100 last:border-0">
                                                        <td className="py-3">
                                                            <div>
                                                                <span className="font-medium text-gray-900">
                                                                    {FIELD_LABELS[field.fieldName] || field.fieldName}
                                                                </span>
                                                                {field.warningText && (
                                                                    <p className="text-xs text-gray-500 mt-0.5">{field.warningText}</p>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 text-center">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                value={effectiveValue}
                                                                onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                                                                className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                                                                    changed
                                                                        ? 'border-amber-400 bg-amber-50'
                                                                        : 'border-gray-300'
                                                                }`}
                                                            />
                                                        </td>
                                                        <td className="py-3 text-center text-gray-500">
                                                            {field.defaultValue}
                                                        </td>
                                                        <td className="py-3 text-right">
                                                            {changed ? (
                                                                <button
                                                                    onClick={() => discardFieldChange(field.fieldName)}
                                                                    className="text-xs text-amber-600 hover:text-amber-700"
                                                                >
                                                                    Discard
                                                                </button>
                                                            ) : isCustomized ? (
                                                                <button
                                                                    onClick={() => setFieldToReset(field.fieldName)}
                                                                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center justify-end space-x-1"
                                                                >
                                                                    <RotateCcw className="w-3 h-3" />
                                                                    <span>Reset</span>
                                                                </button>
                                                            ) : (
                                                                <span className="text-xs text-green-600 flex items-center justify-end space-x-1">
                                                                    <CheckCircle className="w-3 h-3" />
                                                                    <span>Default</span>
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <button
                        onClick={() => setShowResetAllConfirmation(true)}
                        disabled={isSaving || !config.hasAnyCustomizations}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset All to Defaults
                    </button>
                    <div className="flex items-center space-x-3">
                        {hasChanges && (
                            <button
                                onClick={discardAllChanges}
                                disabled={isSaving}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Discard Changes
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={isSaving || !canSave}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: canSave && !isSaving ? '#69BE28' : '#9CA3AF' }}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </div>

            </div>

            {/* Reset All Confirmation Modal */}
            {showResetAllConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset All Scoring Values?</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            This will reset all scoring values to their defaults. Any customisations will be lost.
                            This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowResetAllConfirmation(false)}
                                disabled={isSaving}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResetAll}
                                disabled={isSaving}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                            >
                                {isSaving ? 'Resetting...' : 'Reset All'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Single Field Confirmation Modal */}
            {fieldToReset && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset Field?</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Reset <strong>{FIELD_LABELS[fieldToReset] || fieldToReset}</strong> to its default value?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setFieldToReset(null)}
                                disabled={isSaving}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => resetFieldToDefault(fieldToReset)}
                                disabled={isSaving}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isSaving ? 'Resetting...' : 'Reset'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScoringEditor;
